import React from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { CartProvider } from '../utils/cartContext';
import { WaiterGameProvider } from '../utils/waiterGameContext';
import QRLanding from './QRLanding';
import MenuPage from './MenuPage';
import CartPage from './CartPage';
import LegacyToUniversalRedirect from './LegacyToUniversalRedirect';
import WaiterInviteOverlay from './WaiterInviteOverlay';
import RestaurantLandingPage from './RestaurantLandingPage';
import GamesHub from './games/GamesHub';
import CatchFallingFoodGame from './games/CatchFallingFoodGame';
import FoodMemoryMatchGame from './games/FoodMemoryMatchGame';
import TicTacToeGame from './games/TicTacToeGame';
import { useRestaurantId } from '../utils/restaurantContext';
import './CustomerApp.css';

/**
 * Reusable customer shell that can be mounted under different base routes.
 *
 * Variants:
 * - legacy: /customer/* (keeps existing QRLanding entry)
 * - universal: /menu/:restaurantId/* (new reusable restaurant landing entry)
 */
export default function CustomerShell({ variant = 'legacy' }) {
  return (
    <CartProvider>
      <WaiterGameProvider>
        <CustomerShellInner variant={variant} />
      </WaiterGameProvider>
    </CartProvider>
  );
}

function CustomerShellInner({ variant }) {
  const location = useLocation();
  const restaurantId = useRestaurantId();

  // keep overlay behavior from legacy app (reads restaurantId from URL)
  // and ensure it still works for /menu/:restaurantId/*.

  return (
    <div className="customer-app">
      <Routes>
        {variant === 'legacy' ? (
          <Route path="/" element={<QRLanding />} />
        ) : (
          <Route path="/" element={<RestaurantLandingPage />} />
        )}

        {/* Legacy deep-link support: /customer/menu?restaurantId=... should go to /menu/:restaurantId */}
        {variant === 'legacy' ? (
          <>
            <Route path="/menu" element={<LegacyToUniversalRedirect target="landing" />} />
            <Route path="/cart" element={<LegacyToUniversalRedirect target="cart" />} />
          </>
        ) : (
          <>
            <Route path="/menu" element={<MenuPage />} />
            <Route path="/cart" element={<CartPage />} />
          </>
        )}
        <Route path="/games" element={<GamesHub />} />
        <Route path="/games/catch-the-falling-food" element={<CatchFallingFoodGame />} />
        <Route path="/games/food-memory-match" element={<FoodMemoryMatchGame />} />
        <Route path="/games/tic-tac-toe" element={<TicTacToeGame />} />
      </Routes>

      {/* Rendered outside Routes so it can appear on top of any page */}
      <WaiterInviteOverlay restaurantId={restaurantId} location={location} />
    </div>
  );
}
